import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CheckCircle, Clock, Users, PoundSterling, ExternalLink, Calendar, FileText, HelpCircle, CreditCard, Calculator } from "lucide-react";
import { Button } from "../components/ui/button";
import { childcareData } from "../data/mock";

const Funding = () => {
  const handleApplyClick = () => {
    window.open('https://www.childcarechoices.gov.uk/', '_blank');
  };

  const handleTaxFreeClick = () => {
    window.open('https://www.gov.uk/tax-free-childcare', '_blank');
  };

  const handleGovUKClick = () => {
    window.open('https://www.gov.uk/free-childcare-if-working', '_blank');
  };

  const fundingSteps = [
    {
      step: 1,
      title: "Check Your Eligibility",
      description: "Ensure you meet the working parent requirements and income thresholds",
      timeframe: "5 minutes",
      link: "https://www.childcarechoices.gov.uk/eligibility-checker/"
    },
    {
      step: 2,
      title: "Apply Online",
      description: "Complete your application through the Childcare Choices website",
      timeframe: "15-20 minutes",
      link: "https://www.childcarechoices.gov.uk/apply/"
    },
    {
      step: 3,
      title: "Receive Your Code",
      description: "Get your unique 11-digit code within 7 days of approval",
      timeframe: "Up to 7 days",
      link: null
    },
    {
      step: 4,
      title: "Share with Us",
      description: "Provide your code to Heathbourne House to secure your funded place",
      timeframe: "Immediate",
      link: null
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        
        {/* Hero Section */}
        <section className="py-20 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-bold text-slate-800 mb-6">
                Government 
                <span className="text-blue-500"> Funding Available</span>
              </h1>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
                Get up to 30 hours of free childcare per week for eligible families, or up to 20% off with Tax-Free Childcare. We accept both government funding options and are here to help you maximize your savings.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={handleApplyClick}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-lg"
                >
                  Apply for Free Hours
                  <ExternalLink className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  onClick={handleTaxFreeClick}
                  className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-lg"
                >
                  Tax-Free Childcare
                  <ExternalLink className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Two Simple Options */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              
              {/* Option 1: Free Childcare Hours */}
              <div className="bg-blue-50 rounded-3xl p-8 border-2 border-blue-200">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Clock className="h-10 w-10 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-blue-600 mb-4">Free Childcare Hours</h2>
                  <p className="text-lg text-slate-600">Government-funded childcare hours</p>
                </div>
                
                <div className="space-y-6">
                  <div className="bg-white rounded-xl p-6">
                    <h3 className="font-bold text-slate-800 mb-4">How much is available:</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-700">Ages 9 months - 3 years:</span>
                        <span className="font-bold text-blue-600">Up to 30 hours/week</span>
                      </div>
                      <div className="bg-blue-100 rounded-lg p-3">
                        <p className="text-xs text-blue-800">
                          <strong>From September 2025:</strong> All eligible children 9m-3yrs get 30 hours
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6">
                    <h3 className="font-bold text-slate-800 mb-4">Brief eligibility:</h3>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-start space-x-3">
                        <span className="text-slate-400 mt-1">•</span>
                        <span>Both parents working (or single parent)</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <span className="text-slate-400 mt-1">•</span>
                        <span>Earn minimum 16hrs/week at minimum wage</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <span className="text-slate-400 mt-1">•</span>
                        <span>Household income under £100,000</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6">
                    <h3 className="font-bold text-slate-800 mb-4">How to apply:</h3>
                    <ol className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-start space-x-2">
                        <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                        <span>Visit the Childcare Choices website</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                        <span>Check your eligibility and apply online</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                        <span>Receive your code and share it with us</span>
                      </li>
                    </ol>
                  </div>
                </div>

                <Button 
                  onClick={handleApplyClick}
                  className="w-full mt-8 bg-blue-500 hover:bg-blue-600 text-white py-4 rounded-full text-lg font-semibold transition-all"
                >
                  Apply for Free Hours
                  <ExternalLink className="ml-2 h-5 w-5" />
                </Button>
              </div>

              {/* Option 2: Tax-Free Childcare */}
              <div className="bg-green-50 rounded-3xl p-8 border-2 border-green-200">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CreditCard className="h-10 w-10 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-green-600 mb-4">Tax-Free Childcare</h2>
                  <p className="text-lg text-slate-600">Government pays 20% of your costs</p>
                </div>
                
                <div className="space-y-6">
                  <div className="bg-white rounded-xl p-6">
                    <h3 className="font-bold text-slate-800 mb-4">How much is available:</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-700">All children up to 3 years:</span>
                        <span className="font-bold text-green-600">20% contribution</span>
                      </div>
                      <div className="bg-green-100 rounded-lg p-3">
                        <p className="text-sm text-green-800">
                          <strong>Example:</strong> You pay £8, government adds £2
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6">
                    <h3 className="font-bold text-slate-800 mb-4">Brief eligibility:</h3>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-start space-x-3">
                        <span className="text-slate-400 mt-1">•</span>
                        <span>Both parents working (or single parent)</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <span className="text-slate-400 mt-1">•</span>
                        <span>Each parent earning £2,000-£100,000/year</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <span className="text-slate-400 mt-1">•</span>
                        <span>Can be used alongside other schemes</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6">
                    <h3 className="font-bold text-slate-800 mb-4">How to apply:</h3>
                    <ol className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-start space-x-2">
                        <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                        <span>Apply at gov.uk/tax-free-childcare</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                        <span>Get approved and set up your account</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                        <span>Contact us for our provider code</span>
                      </li>
                    </ol>
                  </div>
                </div>

                <Button 
                  onClick={handleTaxFreeClick}
                  className="w-full mt-8 bg-green-500 hover:bg-green-600 text-white py-4 rounded-full text-lg font-semibold transition-all"
                >
                  Tax-Free Childcare Info
                  <ExternalLink className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Funding;